import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

// components
import SearchModalBase from 'shared/components/SearchModal';
import EventGroupCard from 'events/components/EventGroupCard';

// styles
import * as SearchModalStyles from 'theme/components/SearchModal.style';

// helpers
import {
  buildHighlightRegex,
  extractSnippet,
  getDateFormatWithYear,
  getQueryWords,
} from 'shared/helpers/searchUtils';
import {
  EventSearchGroup,
  getGroupKey,
  groupEventSearchEntries,
} from 'events/helpers/SearchModal.utils';
import { MIN_SEARCH_LENGTH, useEventSearch } from 'events/hooks/useEventSearch';
import { usePosthogCapture } from 'events/posthogCapture/search';

// types
import type { Event, EventTimelineNote } from 'events/_types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventSelect?: (event: Event) => void;
  onNoteSelect?: (event: Event, note: EventTimelineNote) => void;
}

export default function SearchModal({
  isOpen,
  onClose,
  onEventSelect,
  onNoteSelect,
}: SearchModalProps): JSX.Element {
  const { t, i18n } = useTranslation('pageEvents');
  const search = usePosthogCapture();

  const dateFormatWithYear = useMemo(
    () => getDateFormatWithYear(i18n.language),
    [i18n.language]
  );

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const trimmedQuery = debouncedQuery.trim();

  const { data, isFetching } = useEventSearch(debouncedQuery);
  const groups = useMemo<EventSearchGroup[]>(
    () => groupEventSearchEntries(data ?? []),
    [data]
  );

  const filterItems = useCallback(() => groups, [groups]);

  const hasFiredNoResultsRef = useRef(false);
  useEffect(() => {
    if (trimmedQuery.length === 0) {
      hasFiredNoResultsRef.current = false;
      return;
    }
    if (
      trimmedQuery.length >= MIN_SEARCH_LENGTH &&
      !isFetching &&
      groups.length === 0 &&
      !hasFiredNoResultsRef.current
    ) {
      hasFiredNoResultsRef.current = true;
      search.noResults({ query_length: trimmedQuery.length });
    }
  }, [trimmedQuery, isFetching, groups, search]);

  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (!wasOpenRef.current && isOpen) {
      hasFiredNoResultsRef.current = false;
      setQuery('');
    }
    if (wasOpenRef.current && !isOpen) {
      search.closed();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, search]);

  const highlightText = useCallback(
    (text: string, searchQuery: string): React.ReactNode => {
      const words = getQueryWords(searchQuery.trim());
      if (words.length === 0) return text;

      const re = buildHighlightRegex(words);
      if (!re) return text;
      const parts = text.split(re);

      return parts.map((part, idx) => {
        const isMatch = words.includes(part.toLowerCase());
        if (!isMatch) return <React.Fragment key={idx}>{part}</React.Fragment>;
        return (
          <SearchModalStyles.Highlight key={idx}>
            {part}
          </SearchModalStyles.Highlight>
        );
      });
    },
    []
  );

  const handleEventClick = useCallback(
    (event: Event) => {
      search.eventSelected();
      onEventSelect?.(event);
      onClose();
      setQuery('');
    },
    [onEventSelect, onClose, search]
  );

  const handleNoteClick = useCallback(
    (event: Event, note: EventTimelineNote) => {
      search.noteSelected();
      onNoteSelect?.(event, note);
      onClose();
      setQuery('');
    },
    [onNoteSelect, onClose, search]
  );

  const renderGroup = useCallback(
    (
      group: EventSearchGroup,
      searchQuery: string,
      ctx: {
        isSelected: boolean;
        onSelect: () => void;
        onMouseEnter: () => void;
        ref: (el: HTMLElement | null) => void;
      }
    ) => (
      <EventGroupCard
        event={group.event}
        notes={group.notes}
        latestCreated={group.latestCreated}
        dateFormat={dateFormatWithYear}
        onEventClick={handleEventClick}
        onNoteClick={handleNoteClick}
        renderEventTitle={(title) =>
          title ? highlightText(title, searchQuery) : t('search.empty')
        }
        renderEventSummary={(summary) =>
          summary
            ? highlightText(extractSnippet(summary, searchQuery), searchQuery)
            : null
        }
        renderNoteText={(note) => {
          const snippet = extractSnippet(note.edited_text || '', searchQuery);
          return snippet
            ? highlightText(snippet, searchQuery)
            : t('search.empty');
        }}
        cardRef={ctx.ref}
        onMouseEnter={ctx.onMouseEnter}
      />
    ),
    [dateFormatWithYear, handleEventClick, handleNoteClick, highlightText, t]
  );

  const handleItemSelect = useCallback(
    (group: EventSearchGroup) => {
      handleEventClick(group.event);
    },
    [handleEventClick]
  );

  const emptyStateText = useMemo(() => {
    if (isFetching) return t('search.loading');
    const trimmed = query.trim();
    if (trimmed.length === 0) return t('search.hint');
    if (trimmed.length < MIN_SEARCH_LENGTH)
      return t('search.minChars', { count: MIN_SEARCH_LENGTH });
    return t('search.noResults');
  }, [isFetching, query, t]);

  return (
    <SearchModalBase
      isOpen={isOpen}
      onClose={onClose}
      placeholder={t('search.placeholder')}
      noResultsText={emptyStateText}
      onQueryChange={setQuery}
      filterItems={filterItems}
      getItemKey={getGroupKey}
      renderItem={() => null}
      renderRow={renderGroup}
      onItemSelect={handleItemSelect}
    />
  );
}
